import type { GameMinimalProps, Children } from '../../types';

import * as React from 'react';
import styled from '@emotion/styled';
import Grid from '@mui/material/Grid';
import { Card, CardSkeleton } from '../../components/Card';
import { useAppContext } from '../../contexts/AppContext';
import { useTheme } from '@mui/material/styles';
import { CardTypeEnum } from '../../types/enum';

interface SearchResultsGridProps extends Children {
  items?: GameMinimalProps[];
  emptyItems?: number;
  breakpoints?: [];
  className?: string;
}

const CardSkeletonStyled = styled(CardSkeleton)(({ theme }) => ({
  boxShadow: theme.shadows[0],
  aspectRatio: '16/9',
}));

export const SearchResultsGrid = (props: SearchResultsGridProps) => {
  const { items, emptyItems = 0, breakpoints, className, children } = props;
  const theme = useTheme();
  const appContext = useAppContext();
  const { assetUrl, componentOptions } = appContext.useConfig();
  const breaks = {
    xs: 6,
    sm: 4,
    md: 3,
    lg: 2,
    ...breakpoints,
  };
  const games = [...Array(items?.length)].map((game: GameMinimalProps, index: number) => items?.[index]);
  const skeletonAmount = games.length < emptyItems ? emptyItems - games.length : 0;
  const skeletons = [...Array(skeletonAmount)];

  return (
    <div className={className}>
      {children}
      <Grid container spacing={2}>
        {games?.map(
          (game: GameMinimalProps | undefined, index: number) =>
            game?.id && (
              <Grid key={game.id || index} xs={breaks.xs} sm={breaks.sm} md={breaks.md} lg={breaks.lg} item>
                <Card
                  key={index}
                  sx={{ display: 'flex' }}
                  href={`/game/${game.slug}`}
                  src={`${assetUrl}${game.images.cover16x9 || game.images.cover}/`}
                  alt={game.name || 'Cover'}
                  title={game.name || undefined}
                  width={componentOptions.component_card_width || 512}
                  template={CardTypeEnum.DEFAULT}
                  height={componentOptions.component_card_height || 288}
                  sizes={`${theme.breakpoints.up('lg').replace('@media ', '')} 33.333vw, ${theme.breakpoints
                    .up('md')
                    .replace('@media ', '')} 33.333vw, ${theme.breakpoints.up('sm').replace('@media ', '')} 33.333vw, 50vw`}
                  layout="responsive"
                  typographyOptions={{
                    align: 'left',
                  }}
                />
              </Grid>
            )
        )}
        {skeletons.map((skeleton, index) => (
          <Grid key={index} xs={breaks.xs} sm={breaks.sm} md={breaks.md} lg={breaks.lg} item>
            <CardSkeletonStyled elevation={1} />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};
